import React, { useState, useMemo, useEffect } from "react";
import { FaFolder, FaFile, FaTimes, FaArrowLeft } from "react-icons/fa";

import { IMAGE_MIME_TYPES } from "../../constants/allowedFileTypes";

const ALLOWED_MIME_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/plain",
  "application/vnd.oasis.opendocument.text",
  "application/vnd.google-apps.document",
  ...IMAGE_MIME_TYPES,
];

function normalizeDriveData(data) {
  if (!data) return null;
  if (data.root && (data.root.files || data.root.folders)) {
    return data.root;
  }
  if (Array.isArray(data)) {
    return {
      id: "root",
      name: "My Drive",
      webViewLink: null,
      files: data,
      folders: [],
    };
  }
  if (data.documents || data.items) {
    const list = data.documents || data.items;
    return {
      id: "root",
      name: "My Drive",
      webViewLink: null,
      files: list.map((doc) => ({
        id: doc.id,
        name: doc.name || doc.title,
        mimeType: doc.mimeType || doc.mime_type || "",
        webViewLink: doc.webViewLink,
        size: doc.size,
        parents: doc.parents,
      })),
      folders: [],
    };
  }
  return null;
}

function formatSize(size) {
  if (size == null) return "—";
  const n = typeof size === "string" ? parseInt(size, 10) : size;
  if (Number.isNaN(n)) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

// mode:
// - "files": select files (multi-select)
// - "folders": select folders (single-select, for auto ingestion)
const GoogleDrivePicker = ({
  isOpen,
  onClose,
  data,
  onSelect,
  mode = "files",
  title = "Select from Google Drive",
  confirmLabel,
}) => {
  const root = useMemo(() => normalizeDriveData(data), [data]);

  const [breadcrumb, setBreadcrumb] = useState([]);
  const [selectedMap, setSelectedMap] = useState({});
  const [selectedFolder, setSelectedFolder] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setBreadcrumb([]);
      setSelectedMap({});
      setSelectedFolder(null);
    }
  }, [isOpen]);

  const current = useMemo(() => {
    if (!root) return { files: [], folders: [] };
    if (breadcrumb.length === 0) return root;
    const last = breadcrumb[breadcrumb.length - 1];
    return last.folder || { files: [], folders: [] };
  }, [root, breadcrumb]);

  const folders = current.folders || [];
  const files = current.files || [];
  const selectedList = useMemo(
    () => Object.values(selectedMap),
    [selectedMap]
  );
  const selectedCount = selectedList.length;

  const handleOpenFolder = (folder) => {
    setBreadcrumb((prev) => [
      ...prev,
      { id: folder.id, name: folder.name, folder },
    ]);
  };

  const handleBack = () => {
    setBreadcrumb((prev) => (prev.length > 0 ? prev.slice(0, -1) : []));
  };

  const toggleFolder = (folder) => {
    setSelectedFolder((prev) => (prev?.id === folder.id ? null : folder));
  };

  const toggleFile = (file) => {
    const allowed = ALLOWED_MIME_TYPES.includes(
      (file.mimeType || "").toLowerCase()
    );
    if (!allowed) return;
    setSelectedMap((prev) => {
      const next = { ...prev };
      if (next[file.id]) {
        delete next[file.id];
      } else {
        next[file.id] = {
          id: file.id,
          name: file.name,
          mimeType: file.mimeType,
          webViewLink: file.webViewLink,
          size: file.size,
          parents: file.parents,
        };
      }
      return next;
    });
  };

  const handleConfirm = () => {
    if (mode === "folders") {
      if (selectedFolder && onSelect) {
        onSelect([
          {
            id: selectedFolder.id,
            name: selectedFolder.name,
            webViewLink: selectedFolder.webViewLink,
          },
        ]);
      }
    } else if (selectedCount > 0 && onSelect) {
      onSelect(selectedList);
    }
    setSelectedMap({});
    setBreadcrumb([]);
    setSelectedFolder(null);
    onClose?.();
  };

  const handleCancel = () => {
    setSelectedMap({});
    setBreadcrumb([]);
    setSelectedFolder(null);
    onClose?.();
  };

  const isFileAllowed = (file) =>
    ALLOWED_MIME_TYPES.includes((file.mimeType || "").toLowerCase());

  if (!isOpen) return null;
  if (!root) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000]">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl p-6">
          <p className="text-gray-600 dark:text-gray-300">
            No drive data available.
          </p>
          <button
            type="button"
            onClick={handleCancel}
            className="mt-4 px-4 py-2 bg-gray-200 dark:bg-gray-600 rounded hover:opacity-90"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000]">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-white">
            {title}
          </h2>
          <button
            type="button"
            onClick={handleCancel}
            className="p-1.5 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded"
            aria-label="Close"
          >
            <FaTimes className="w-5 h-5" />
          </button>
        </div>

        {/* Breadcrumb */}
        <div className="flex items-center gap-2 px-4 py-3 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-600 flex-shrink-0">
          {breadcrumb.length > 0 && (
            <button
              type="button"
              onClick={handleBack}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded hover:bg-gray-100 dark:hover:bg-gray-500 transition-colors"
            >
              <FaArrowLeft className="w-3.5 h-3.5" />
              Back
            </button>
          )}
          <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 min-w-0">
            <FaFolder className="w-4 h-4 text-amber-500 flex-shrink-0" />
            <span className="font-medium truncate">
              {breadcrumb.length > 0
                ? breadcrumb[breadcrumb.length - 1].name
                : current.name || "My Drive"}
            </span>
          </div>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto p-4 min-h-0">
          {folders.length === 0 && files.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500 dark:text-gray-400">
              <FaFolder className="w-14 h-14 mb-2 opacity-50" />
              <p>This folder is empty</p>
            </div>
          ) : (
            <div className="space-y-1">
              {folders.map((folder) => (
                <button
                  key={folder.id}
                  type="button"
                  onClick={() => handleOpenFolder(folder)}
                  className="w-full flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-left"
                >
                  <FaFolder className="w-7 h-7 text-amber-500 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-800 dark:text-white truncate">
                      {folder.name}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Folder
                    </p>
                  </div>
                  {mode === "folders" && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        toggleFolder(folder);
                      }}
                      className={`w-9 h-9 rounded-md border flex items-center justify-center ${selectedFolder?.id === folder.id
                        ? "bg-blue-600 border-blue-600 text-white"
                        : "border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-200"
                        }`}
                      aria-label="Select folder"
                      title="Select folder"
                    >
                      {selectedFolder?.id === folder.id ? "✓" : ""}
                    </button>
                  )}
                </button>
              ))}
              {mode !== "folders" && files.map((file) => {
                const allowed = isFileAllowed(file);
                const selected = !!selectedMap[file.id];
                return (
                  <button
                    key={file.id}
                    type="button"
                    onClick={() => toggleFile(file)}
                    disabled={!allowed}
                    className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-colors text-left ${selected
                      ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
                      : "border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
                      } ${!allowed ? "opacity-60 cursor-not-allowed" : ""}`}
                  >
                    <div
                      className={`w-5 h-5 rounded border-2 flex-shrink-0 flex items-center justify-center ${selected
                        ? "bg-blue-500 border-blue-500"
                        : "border-gray-400 dark:border-gray-500"
                        }`}
                    >
                      {selected && (
                        <span className="text-white text-xs font-bold">✓</span>
                      )}
                    </div>
                    <FaFile className="w-6 h-6 text-gray-500 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-800 dark:text-white truncate">
                        {file.name}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {file.mimeType || "File"} · {formatSize(file.size)}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {mode === "folders"
              ? selectedFolder
                ? `Selected folder: ${selectedFolder.name}`
                : "Select a folder to enable auto ingestion"
              : selectedCount > 0
                ? `${selectedCount} file(s) selected`
                : "PDF, DOC, DOCX, XLS, XLSX, TXT, JPG, PNG supported"}
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleCancel}
              className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={mode === "folders" ? !selectedFolder : selectedCount === 0}
              className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {confirmLabel || (mode === "folders" ? "Apply auto ingestion" : "Upload selected")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GoogleDrivePicker;
export { normalizeDriveData, ALLOWED_MIME_TYPES };
